/**
 * lib/textbooks.ts
 * ------------------------------------------------------------------
 * Textbook Issue & Returns (proposal LKC-PROP-2026-004, module 5) — the
 * pure part: what a book, a loan and a condition are, and the few rules
 * that decide whether a loan is valid and what a lost book costs.
 *
 * Three collections:
 *   textbooks/{bookId}          one shared school book list; any teacher adds
 *   textbookLoans/{loanId}      one copy lent to one pupil, issue to return
 *   textbookCopies/{bookId__copyKey}
 *                               a lock that exists only while that copy is out,
 *                               so the same copy can never be lent twice
 *                               (firestore.rules refuses a second create).
 *
 * Parents and pupils read loans directly, by studentId, and NOT through
 * getChildAcademics: a book on loan is something the family owes, so the
 * fees hold does not hide it (the school's decision, 30 Oct 2026).
 *
 * A lost or unusable book waits at bursary 'pending' until the bursar charges
 * the family (one manual invoice, see HIGH-RISK-CHANGES.md §17) or waives it.
 * ------------------------------------------------------------------ */
import { round2, type InvoiceLine } from '@/lib/financeCore';

export const TEXTBOOK_COLLECTIONS = {
  books: 'textbooks',
  loans: 'textbookLoans',
  copies: 'textbookCopies',
} as const;

/* ------------------------------ condition ------------------------------ */

export type BookCondition = 'new' | 'good' | 'fair' | 'poor' | 'unusable' | 'lost';

export interface ConditionInfo {
  id: BookCondition;
  label: string;
  /** What a teacher should see in the book to choose this grade. */
  description: string;
  /** May a book be ISSUED in this state? Unusable and lost are return-only. */
  issuable: boolean;
}

/** Best to worst. The order is what "the book came back worse" is measured on. */
export const CONDITIONS: ConditionInfo[] = [
  { id: 'new', label: 'New', issuable: true,
    description: 'Never issued. No marks, no wear.' },
  { id: 'good', label: 'Good', issuable: true,
    description: 'Lightly used. Clean pages, cover and spine intact; a name or stamp is fine.' },
  { id: 'fair', label: 'Fair', issuable: true,
    description: 'Clearly used. Worn cover or corners, some writing or highlighting; all pages present, binding firm.' },
  { id: 'poor', label: 'Poor', issuable: true,
    description: 'Heavily used. Loose or torn pages, damaged cover or spine, or heavy writing — but still usable.' },
  { id: 'unusable', label: 'Unusable', issuable: false,
    description: 'Too damaged to issue again.' },
  { id: 'lost', label: 'Lost', issuable: false,
    description: 'Not returned.' },
];

export const ISSUE_CONDITIONS = CONDITIONS.filter((c) => c.issuable);
/** On return a teacher grades the book; "lost" is its own action, not a grade. */
export const RETURN_CONDITIONS = CONDITIONS.filter((c) => c.id !== 'lost');

export function conditionLabel(c: BookCondition | undefined | null): string {
  return CONDITIONS.find((x) => x.id === c)?.label ?? '—';
}

/** How many grades worse the book came back (0 when the same or better). */
export function conditionDrop(out: BookCondition, back: BookCondition): number {
  const rank = (c: BookCondition) => CONDITIONS.findIndex((x) => x.id === c);
  return Math.max(0, rank(back) - rank(out));
}

/* -------------------------------- types -------------------------------- */

export interface Textbook {
  id: string;
  title: string;
  author?: string;
  /** As typed, normalised to digits (and a trailing X) by normaliseIsbn. */
  isbn?: string;
  subjectId?: string;
  subjectName?: string;
  /** Forms the book is used in ('1'..'5', 'AS', 'A2' — SchoolClass.form). */
  forms?: string[];
  edition?: string;
  publisher?: string;
  /** Pula. What the bursar charges for a lost or ruined copy. */
  replacementCost?: number;
  active: boolean;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export type LoanStatus = 'out' | 'returned' | 'lost' | 'unusable';
export type BursaryState = 'pending' | 'charged' | 'waived';

export interface TextbookLoan {
  id: string;
  bookId: string;
  /** Copied from the book at issue, so a parent's view needs no catalogue read
   *  and a later edit to the book does not rewrite history. */
  title: string;
  isbn?: string;
  subjectId?: string;
  subjectName?: string;
  /** As the teacher typed or scanned it. */
  copyNo: string;
  /** copyKey(copyNo) — what uniqueness is decided on. */
  copyKey: string;
  studentId: string;
  studentName: string;
  classId: string;
  className: string;
  form?: string;
  status: LoanStatus;
  conditionOut: BookCondition;
  conditionIn?: BookCondition;
  issuedAt: string;
  issuedBy: string;
  issuedByName?: string;
  dueDate?: string;
  returnedAt?: string;
  returnedBy?: string;
  returnedByName?: string;
  /** Snapshot at issue; the bursar may still charge a different amount. */
  replacementCost?: number;
  bursary?: BursaryState;
  bursaryAt?: string;
  bursaryBy?: string;
  bursaryByName?: string;
  waiveReason?: string;
  charge?: { invoiceId: string; invoiceNo: string; amount: number };
  updatedAt?: string;
}

/* ---------------------------- copy numbers ----------------------------- */

/**
 * The form of a copy number that uniqueness is decided on.
 *
 * "phy 042", "PHY-042" and " PHY 042 " are the same sticker typed three ways,
 * so case and spacing are ignored. A slash cannot appear in a Firestore id,
 * and a stamped "PHY/042" is common, so it becomes a dash.
 */
export function copyKey(raw: string): string {
  return raw
    .trim()
    .toUpperCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[/\\#?.[\]]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function copyDocId(bookId: string, key: string): string {
  return `${bookId}__${key}`;
}

/**
 * Rows on one issue sheet that name the same copy. The lock catches a copy
 * already out; this catches the teacher typing one number on two pupils
 * before anything is saved.
 */
export function duplicateCopies(rows: { studentId: string; copyNo: string }[]): Set<string> {
  const seen = new Map<string, string>();
  const dup = new Set<string>();
  for (const r of rows) {
    const k = copyKey(r.copyNo);
    if (!k) continue;
    const prev = seen.get(k);
    if (prev) { dup.add(prev); dup.add(r.studentId); } else seen.set(k, r.studentId);
  }
  return dup;
}

/* -------------------------------- ISBN -------------------------------- */

export function normaliseIsbn(raw: string): string {
  return raw.toUpperCase().replace(/[^0-9X]/g, '');
}

/** A check-digit test, so a mistyped ISBN is caught at the desk. */
export function isValidIsbn(raw: string): boolean {
  const s = normaliseIsbn(raw);
  if (/^\d{9}[\dX]$/.test(s)) {
    const sum = s.split('').reduce((a, ch, i) => a + (ch === 'X' ? 10 : Number(ch)) * (10 - i), 0);
    return sum % 11 === 0;
  }
  if (/^\d{13}$/.test(s)) {
    const sum = s.split('').reduce((a, ch, i) => a + Number(ch) * (i % 2 ? 3 : 1), 0);
    return sum % 10 === 0;
  }
  return false;
}

/* ------------------------------- dates -------------------------------- */

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july',
  'august', 'september', 'october', 'november', 'december'];

/**
 * The last day of the school year, from the term calendar's display string
 * ("15 September – 9 December"). Textbooks are lent for the year, so this is
 * the default due date. Falls back to 31 December when the string will not parse.
 */
export function yearEndDue(year: string, lastTermDates: string): string {
  const end = lastTermDates.split(/[–-]/).pop()?.trim().toLowerCase() ?? '';
  const m = end.match(/^(\d{1,2})\s+([a-z]+)/);
  const mi = m ? MONTHS.indexOf(m[2]) : -1;
  if (!m || mi < 0) return `${year}-12-31`;
  return `${year}-${String(mi + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

export function isOverdue(loan: Pick<TextbookLoan, 'status' | 'dueDate'>, todayIso: string): boolean {
  return loan.status === 'out' && !!loan.dueDate && loan.dueDate < todayIso.slice(0, 10);
}

/* ------------------------------- charges ------------------------------- */

/** A loan the bursar has still to decide on. */
export function awaitsBursar(loan: Pick<TextbookLoan, 'status' | 'bursary'>): boolean {
  return (loan.status === 'lost' || loan.status === 'unusable') && loan.bursary === 'pending';
}

/**
 * The single invoice line for a lost or ruined copy. The amount is what the
 * bursar confirmed, rounded to the thebe; nothing is charged for zero.
 */
export function chargeLine(
  loan: Pick<TextbookLoan, 'title' | 'copyNo' | 'status'>,
  amount: number,
): InvoiceLine {
  const a = round2(Number(amount) || 0);
  if (a <= 0) throw new Error('A replacement charge must be more than P0.');
  const why = loan.status === 'lost' ? 'lost' : 'returned unusable';
  return {
    label: `Replacement textbook — ${loan.title} (copy ${loan.copyNo}, ${why})`,
    category: 'other',
    amount: a,
  };
}

/** Deterministic, so a retried charge lands on the same invoice id. */
export function chargeInvoiceId(year: string, term: string, studentId: string, loanId: string): string {
  return `${year}_${term}_${studentId}_book_${loanId}`;
}
